import { Link } from "react-router-dom";
import { Seo } from "../lib/seo";
import { AiActStatus } from "../components/AiActStatus";
import { NumberedArguments } from "../components/NumberedArguments";

const arguments_ = [
  {
    title: "Article 50 has applied since 2 August 2026.",
    body: [
      "Any AI system intended to interact directly with people must tell them they are dealing with AI, from the start of the first interaction, unless that is obvious. For a bank running a customer-facing assistant, that duty is live today and it is not deferred.",
      "It is also a narrow duty. Article 50 asks for disclosure, not a conformity assessment. Bancony ships the disclosure in the channel UI, which means the article that actually binds your assistant today is handled before you start.",
    ],
  },
  {
    title: "The high-risk regime was deferred, not cancelled.",
    body: [
      "Regulation (EU) 2026/1744, the Digital Omnibus on AI, moved the Chapter III obligations for Annex III systems from 2 August 2026 to 2 December 2027, and for Annex I systems from 2 August 2027 to 2 August 2028. It has been in force since 27 July 2026.",
      "Recital 40 gives the reason: standards, common specifications and national competent authorities were not ready. Nothing about the substance of the obligations changed. Banks that read the deferral as a reprieve will meet the same requirements in 2027 with less time to prepare than they have now.",
    ],
  },
  {
    title: "Where the high-risk line actually falls for a bank.",
    body: [
      "Annex III point 5(b) covers AI used to evaluate the creditworthiness of natural persons or establish their credit score, with an exception for fraud detection. Balance queries, transaction history, recipient management and payment initiation sit outside it.",
      "The line is closer than it looks. A system does not have to make the lending decision to be caught: producing a score or a recommendation that a human underwriter then acts on is enough. Most assistants start below the threshold and drift toward it as scope grows.",
    ],
  },
  {
    title: "The scaffolding is the same on either side of the line.",
    body: [
      "Article 12-15 record-keeping, Article 14 human oversight and Article 17 quality management bind high-risk systems. They do not bind a balance-and-transfers assistant today, and honest vendors should say so rather than sell them as current obligations.",
      "Bancony ships them anyway. A bank that deploys now accumulates two years of conversation-level evidence, oversight checkpoints and QMS documentation before December 2027. If scope later reaches creditworthiness, that date becomes a classification exercise rather than a rebuild.",
    ],
  },
  {
    title: "Article 4 was softened, not removed.",
    body: [
      "The omnibus rewrote the AI literacy duty. Providers and deployers no longer have to \"ensure\" a sufficient level of AI literacy; they must \"take measures to support the development\" of it, and the text now says explicitly that it does not require guaranteeing any specific level for any individual. That version applies from 27 July 2026.",
      "It is still binding on every deployer, and it is now an obligation of effort rather than of result. That makes it easier to satisfy on paper and harder to evidence convincingly. Operating a real system, with real procurement reviews and real runbooks, remains the most defensible evidence a bank can produce.",
    ],
  },
  {
    title: "The runway is where supervisory guidance gets written.",
    body: [
      "BaFin, ACPR, the FCA and the Central Bank will write their AI-banking expectations across the deferral window, alongside the standards whose absence caused the deferral in the first place. Banks that are operating during that window shape what lands; banks that are still selecting vendors inherit it.",
      "Bancony's hosted Langfuse compounds the same way. The Assistant improves monthly on anonymised signal across every bank on the platform, so early banks carry a quality advantage that later ones cannot buy back.",
    ],
  },
];

export const AiActAugust2026 = () => (
  <>
    <Seo
      title="EU AI Act and banking chatbots: what applies now, what lands in 2027"
      description="Article 50 has bound customer-facing banking assistants since 2 August 2026. The Digital Omnibus moved the high-risk regime to 2 December 2027. What that means for a bank deploying an assistant."
      path="/ai-act-august-2026"
    />

    <section className="page-hero">
      <div className="page-hero-inner">
        <span className="section-eyebrow">EU AI Act, where it stands</span>
        <h1>Article 50 is live. The high-risk regime lands in 2027.</h1>
        <p className="page-hero-sub">
          The Digital Omnibus deferred the AI Act's high-risk obligations by sixteen
          months but left the transparency duties untouched. For a bank running a
          customer-facing assistant, that split decides what you owe today and what you
          should be building toward.
        </p>
        <AiActStatus />
        <div className="home-hero-ctas">
          <Link to="/contact?intent=ai-act-2026" className="btn btn-primary">
            Get the AI Act readiness spec sheet
          </Link>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">The argument</span>
        <h2>Six things that are true today.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          The deferral changed the calendar, not the requirements. These six points are
          the difference between a bank that spends the runway and a bank that discovers
          in late 2027 that it still has the same work to do.
        </p>
        <NumberedArguments items={arguments_} />
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">What it takes</span>
        <h2>What a deployment actually takes.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          Roughly eight weeks of implementation from contract signature, and in practice
          the AI Act is rarely the constraint. DORA has applied since 17 January 2025 and
          makes Bancony a contracted ICT third party, so the Article 30 clause review and
          the Register of Information entry usually set the pace. Below is the honest
          read on which banks move fast.
        </p>

        <div className="regulation-split" style={{ marginTop: 24 }}>
          <div className="regulation-block">
            <h4>Banks that can</h4>
            <ul>
              <li>Executive sponsorship already in place.</li>
              <li>Defined initial use case (typical: customer-facing FAQ + balance and transaction queries + recipient management; no transfers in v1).</li>
              <li>Core banking integration team aligned and reachable.</li>
              <li>DPO available for a DORA Article 30 contract review within two weeks.</li>
            </ul>
          </div>
          <div className="regulation-block">
            <h4>Banks that probably can't</h4>
            <ul>
              <li>Still building the business case internally.</li>
              <li>No designated executive sponsor.</li>
              <li>Waiting on the annual budget cycle to refresh.</li>
              <li>Procurement reviews that run six months or longer by default.</li>
            </ul>
          </div>
        </div>

        <p style={{ marginTop: 28, color: "var(--ink-mute)" }}>
          That's not a problem. Bancony serves both. But this page is for the first group.
        </p>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <div className="final-cta">
          <div>
            <h2>Book a 30-minute readiness call.</h2>
            <p>The call classifies your intended scope against Annex III, separates what Article 50 requires of you today from what December 2027 will, and produces a written timeline. Yours to keep regardless of whether we work together.</p>
          </div>
          <div className="final-cta-cta">
            <Link to="/contact?intent=ai-act-readiness" className="btn btn-primary">Book a readiness call</Link>
          </div>
        </div>
      </div>
    </section>
  </>
);
